import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

function money(v, digits = 2) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function pct(v, { asFraction = false } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const val = asFraction ? n * 100 : n;
  const sign = val > 0 ? '+' : '';
  return `${sign}${val.toFixed(2)}%`;
}

function num(v, digits = 2) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function Stat({ label, value }) {
  return (
    <div className="min-w-0 flex justify-between gap-2 text-[11px]">
      <span className="text-muted shrink-0">{label}</span>
      <span className="font-mono tabular-nums text-ink truncate text-right">{value}</span>
    </div>
  );
}

/**
 * Mutual-fund / ETF fundamentals.
 * variant="quote" — compact strip for QuotePanel (NAV-first, fund fields)
 * default — standalone card
 */
export default function MutualFundPanel({
  symbol,
  assetType,
  variant = 'default',
  hideDuplicateHeader = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const isFund = String(assetType || '').toUpperCase() === 'MUTUAL_FUND';
  const compact = variant === 'quote';

  useEffect(() => {
    if (!symbol) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await marketDataApi.getInstrumentOverview(symbol);
        if (cancelled) return;
        setData(res?.data || res);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(err.response?.data?.error || err.message || 'Fund data unavailable');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  if (!symbol) return null;

  const instrument = data?.instrument;
  const quote = data?.quote;
  const fund = instrument?.fundamental || {};
  const type = String(instrument?.assetType || assetType || '').toUpperCase();
  const show = isFund || type === 'MUTUAL_FUND' || type === 'ETF';

  if (!show && !loading) return null;

  const last = quote?.lastPrice ?? quote?.closePrice ?? quote?.nav ?? quote?.askPrice ?? quote?.bidPrice;
  const change = quote?.netChange;
  const changePct = quote?.netPercentChange;
  const bull = Number(change) >= 0;
  const isMutual = type === 'MUTUAL_FUND' || isFund;

  if (compact) {
    return (
      <div className="rounded-md border border-hairline bg-canvas overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 border-b border-hairline">
          <div className="lt-label">{isMutual ? 'NAV · Fund' : 'Last · ETF'}</div>
          {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted text-[10px]" />}
        </div>

        {error && !loading ? (
          <div className="px-2.5 py-2 text-[11px] text-muted">{error}</div>
        ) : (
          <div className="px-2.5 py-2 space-y-2">
            {!hideDuplicateHeader && (
              <div className="text-[12px] font-medium text-ink truncate">
                {instrument?.name || instrument?.description || symbol}
              </div>
            )}

            <div className="flex items-baseline gap-2 font-mono tabular-nums flex-wrap">
              <span className="text-[18px] font-semibold text-ink leading-none">{money(last)}</span>
              <span className={`text-[12px] font-semibold ${bull ? 'text-bull' : 'text-bear'}`}>
                {money(change)} ({pct(changePct)})
              </span>
              {isMutual && <span className="text-[10px] text-muted uppercase tracking-wider">EOD</span>}
            </div>

            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              {isMutual ? (
                <>
                  <Stat label="CUSIP" value={instrument?.cusip || '—'} />
                  <Stat label="Exchange" value={instrument?.exchange || '—'} />
                  <Stat label="52w high" value={money(fund.high52)} />
                  <Stat label="52w low" value={money(fund.low52)} />
                  <Stat label="Div yield" value={pct(fund.dividendYield, { asFraction: true })} />
                  <Stat label="Div amt" value={money(fund.dividendAmount)} />
                </>
              ) : (
                <>
                  <Stat label="52w high" value={money(fund.high52)} />
                  <Stat label="52w low" value={money(fund.low52)} />
                  <Stat label="Div yield" value={pct(fund.dividendYield, { asFraction: true })} />
                  <Stat label="P/E" value={num(fund.peRatio)} />
                  <Stat label="Beta" value={num(fund.beta)} />
                  <Stat label="Exchange" value={instrument?.exchange || '—'} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-hairline bg-canvas px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="lt-label">
          {type === 'MUTUAL_FUND' ? 'Mutual fund' : type === 'ETF' ? 'ETF' : 'Instrument'}
        </div>
        {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted text-xs" />}
      </div>

      {error && !loading ? (
        <div className="text-[11px] text-muted">{error}</div>
      ) : (
        <>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-semibold text-ink text-sm truncate max-w-full">
              {instrument?.name || instrument?.description || symbol}
            </span>
            {type ? (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 font-semibold">
                {type === 'MUTUAL_FUND' ? 'Fund' : type}
              </span>
            ) : null}
          </div>

          <div className="flex items-baseline gap-2 font-mono tabular-nums">
            <span className="text-lg font-semibold text-ink">{money(last)}</span>
            <span className={`text-xs ${bull ? 'text-[var(--lt-bull)]' : 'text-[var(--lt-bear)]'}`}>
              {money(change)} ({pct(changePct)})
            </span>
          </div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
            <Stat label="CUSIP" value={instrument?.cusip || '—'} />
            <Stat label="Exchange" value={instrument?.exchange || '—'} />
            <Stat label="52w high" value={money(fund.high52)} />
            <Stat label="52w low" value={money(fund.low52)} />
            <Stat label="Div yield" value={pct(fund.dividendYield, { asFraction: true })} />
            <Stat label="Div amt" value={money(fund.dividendAmount)} />
            {!isMutual && (
              <>
                <Stat label="P/E" value={num(fund.peRatio)} />
                <Stat label="Beta" value={num(fund.beta)} />
              </>
            )}
          </div>

          <div className="text-[10px] text-muted">Data via Schwab Market Data</div>
        </>
      )}
    </div>
  );
}
